import { FormattedMessage, useIntl } from 'react-intl';
import { Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift } from '@fortawesome/free-solid-svg-icons';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';

import { IUserWalletRewardItem } from '@share/common-types';
import { RewardWalletPriority } from '@share/common-types';
import { Routes } from '@share/constants';
import { getNextRewardByPriority } from '@utils';

import './style.scss';

const NEXT_AVAILABLE_TOOLTIP_IDS: Record<RewardWalletPriority.Highest | RewardWalletPriority.Cheapest, string> = {
  [RewardWalletPriority.Highest]: 'wallet.reward.next_available.tooltip.highest',
  [RewardWalletPriority.Cheapest]: 'wallet.reward.next_available.tooltip.cheapest',
};

interface IProps {
  allRewards: IUserWalletRewardItem[];
  nextAvailableReward?: IUserWalletRewardItem;
  priority: RewardWalletPriority | undefined;
  account?: { walletNoDecimals?: boolean; name?: string } | null;
}

function formatRewardValue(
  value: number | undefined,
  account: { walletNoDecimals?: boolean } | null | undefined
): string {
  if (value == null) return '—';
  const num = account?.walletNoDecimals ? Math.floor(value) : Math.floor(value * 100) / 100;
  return `$${num.toLocaleString('en-US', {
    minimumFractionDigits: account?.walletNoDecimals ? 0 : 2,
    maximumFractionDigits: account?.walletNoDecimals ? 0 : 2,
  })}`;
}

export function RewardsNextAvailable(props: IProps) {
  const { allRewards, nextAvailableReward, priority, account } = props;
  const intl = useIntl();

  const showNextAvailable =
    (priority === RewardWalletPriority.Highest || priority === RewardWalletPriority.Cheapest) &&
    priority in NEXT_AVAILABLE_TOOLTIP_IDS;
  const nextReward = showNextAvailable
    ? getNextRewardByPriority(
        allRewards,
        priority as RewardWalletPriority.Highest | RewardWalletPriority.Cheapest,
        nextAvailableReward,
      )
    : undefined;

  if (nextReward == null) {
    return null;
  }

  const tooltipTitle = intl.formatMessage({
    id: NEXT_AVAILABLE_TOOLTIP_IDS[priority as RewardWalletPriority.Highest | RewardWalletPriority.Cheapest],
  });
  const expirationMoment = nextReward.expirationDate ? moment(nextReward.expirationDate) : null;
  const expirationFormatted = expirationMoment?.isValid() ? expirationMoment.format('MM/DD/YYYY') : '—';
  const daysLeft =
    nextReward.daysUntilExpiration != null
      ? nextReward.daysUntilExpiration
      : expirationMoment?.isValid()
        ? Math.max(0, expirationMoment.diff(moment(), 'days'))
        : null;
  const urgencyLevel = daysLeft != null ? (daysLeft <= 7 ? 'urgent' : daysLeft <= 30 ? 'soon' : null) : null;
  const searchHref = account?.name ? `/${account.name}${Routes.Search}` : null;
  const amountFormatted = formatRewardValue(nextReward.rewardValue, account);

  return (
    <div className="rewards-next-available">
      <div className={`rewards-next-available__card ${urgencyLevel ? `rewards-next-available__card--${urgencyLevel}` : ''}`}>
        <FontAwesomeIcon icon={faGift} className="rewards-next-available__icon" />
        <div className="rewards-next-available__content">
          <div className="rewards-next-available__header">
            <span className="rewards-next-available__title">
              <FormattedMessage id="wallet.reward.next_available" />
            </span>
            <Tooltip title={tooltipTitle} overlayStyle={{ zIndex: 4100 }} overlayClassName="rewards-next-available-tooltip">
              <FontAwesomeIcon icon={faCircleInfo} className="rewards-next-available__info-icon" />
            </Tooltip>
          </div>
          <div className="rewards-next-available__row">
            <span className="rewards-next-available__label">
              <FormattedMessage id="wallet.reward.amount" />:
            </span>
            <span className="rewards-next-available__value">{amountFormatted}</span>
          </div>
          <div className={`rewards-next-available__row rewards-next-available__expiry ${urgencyLevel ? `rewards-next-available__expiry--${urgencyLevel}` : ''}`}>
            {daysLeft != null ? (
              <>
                <span className="rewards-next-available__label">
                  <FormattedMessage id="rewards.next.expiresIn" values={{ days: daysLeft }} />
                </span>
                <span className="rewards-next-available__value">
                  <FormattedMessage id="rewards.next.useBefore" values={{ date: expirationFormatted }} />
                </span>
              </>
            ) : (
              <>
                <span className="rewards-next-available__label">
                  <FormattedMessage id="wallet.reward.expiration_date" />:
                </span>
                <span className="rewards-next-available__value">{expirationFormatted}</span>
              </>
            )}
          </div>
          {searchHref && (
            <Link
              to={searchHref}
              className="rewards-next-available__cta"
            >
              <FormattedMessage id="rewards.next.viewHotelsCta" values={{ amount: amountFormatted }} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
